import { useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router';
import { showErrorToast } from '@jarvis/platform-ui';
import { DashboardCardLinkButton } from '@jarvis/platform-ui/storage-dashboard';
import { MyAppsIcon } from '../../apps/myAppsIcon';
import { launchMyAppsEntry } from '../../apps/myAppsLaunch';
import { BROWSE_ALL_APPS_PATH, buildMyAppsShortcutLeaves } from '../../apps/myAppsNav';
import { getPermittedSearchEntries } from '../../apps/myAppsSearch';
import { useMyAppsStore } from '../../apps/useMyAppsStore';
import { useAuthStore } from '../../auth/useAuthStore';
import { useSiteCapabilitiesStore } from '../../site/useSiteCapabilitiesStore';
import './MyAppsShortcutsCard.css';

export const MY_APPS_SHORTCUTS_CARD_ID = 'my-apps-shortcuts';

export function MyAppsShortcutsCard() {
  const navigate = useNavigate();
  const roles = useAuthStore((s) => s.roles);
  const analyticsEnabled = useSiteCapabilitiesStore((s) => s.analyticsEnabled);
  const status = useMyAppsStore((s) => s.status);
  const error = useMyAppsStore((s) => s.error);
  const pinnedToolIds = useMyAppsStore((s) => s.pinnedToolIds);
  const externalApps = useMyAppsStore((s) => s.externalApps);
  const load = useMyAppsStore((s) => s.load);

  useEffect(() => {
    if (status === 'idle' || status === 'error') void load();
  }, [load, status]);

  const entries = useMemo(
    () => getPermittedSearchEntries(roles, externalApps, analyticsEnabled),
    [analyticsEnabled, externalApps, roles]
  );
  const entriesById = useMemo(() => new Map(entries.map((entry) => [entry.id, entry])), [entries]);
  const customAppIds = useMemo(
    () => entries.filter((entry) => entry.kind === 'link').map((entry) => entry.id),
    [entries]
  );
  const shortcuts = useMemo(
    () => buildMyAppsShortcutLeaves(pinnedToolIds, entriesById, customAppIds),
    [customAppIds, entriesById, pinnedToolIds]
  );

  return (
    <section
      className="dashboard-card dashboard-card--storage my-apps-shortcuts-card"
      aria-label="My Apps"
    >
      <div className="dashboard-card__header">
        <div>
          <h3 className="dashboard-card__title">My Apps</h3>
          <p>Pinned and custom apps</p>
        </div>
        <DashboardCardLinkButton text="Browse apps" href={BROWSE_ALL_APPS_PATH} />
      </div>

      <div className="dashboard-kpi-row">
        <div className="dashboard-kpi">
          <span className="dashboard-kpi__value">
            {status === 'ready' ? pinnedToolIds.length : '—'}
          </span>
          <span className="dashboard-kpi__label">Pinned</span>
        </div>
        <div className="dashboard-kpi">
          <span className="dashboard-kpi__value">
            {status === 'ready' ? entriesById.size : '—'}
          </span>
          <span className="dashboard-kpi__label">Available to you</span>
        </div>
      </div>

      <div className="dashboard-card__section my-apps-shortcuts-card__section">
        <h4 className="dashboard-card__section-title">Shortcuts</h4>
        {status === 'loading' || status === 'idle' ? (
          <p className="dashboard-empty" role="status">
            Loading shortcuts…
          </p>
        ) : null}

        {status === 'error' ? (
          <div className="dashboard-empty">
            <p>{error ?? 'Could not load pinned apps.'}</p>
            <button
              type="button"
              className="my-apps-shortcuts-card__retry"
              onClick={() => {
                void load().catch((err) =>
                  showErrorToast(err instanceof Error ? err.message : 'Could not load My Apps.')
                );
              }}
            >
              Retry
            </button>
          </div>
        ) : null}

        {status === 'ready' && shortcuts.length === 0 ? (
          <p className="dashboard-empty">
            Pin apps you use often from <Link to={BROWSE_ALL_APPS_PATH}>Browse and manage apps</Link>.
          </p>
        ) : null}

        {status === 'ready' && shortcuts.length > 0 ? (
          <ul className="my-apps-shortcuts-card__grid" aria-label="My Apps shortcuts">
            {shortcuts.map((entry) => {
              const app = entriesById.get(entry.id);
              return (
                <li key={entry.id}>
                  <button
                    type="button"
                    className="my-apps-shortcuts-card__tile"
                    title={entry.text}
                    onClick={() => {
                      if (
                        !launchMyAppsEntry(
                          {
                            id: entry.id,
                            kind: app?.kind ?? 'tool',
                            path: entry.path,
                            url: entry.url ?? app?.url
                          },
                          navigate
                        )
                      ) {
                        showErrorToast('Could not open this app.');
                      }
                    }}
                  >
                    <MyAppsIcon
                      icon={app?.icon ?? ''}
                      className="my-apps-shortcuts-card__tile-icon"
                    />
                    <span>{entry.text}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    </section>
  );
}
